import { useState, useRef, useEffect, useMemo, useCallback, type FC } from "react";
import { useBlocker } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { LawyerLayout } from "@/pages/layouts/LawyerLayout";
import { useAppContext } from "@/shared/hooks/useAppContext";
import {
  useLawyerSettings,
  useLawyerLicensedStates,
  useLawyerPhotoUrl,
} from "@/shared/hooks/useLawyerSettings";
import { cn } from "@/shared/utils/core";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";

import { ProfileSection, type ProfileFormData } from "./components/ProfileSection";
import { NotificationsSection } from "./components/NotificationsSection";
import { SecuritySection } from "./components/SecuritySection";
import { HelpSection } from "./components/HelpSection";
import { ProfilePreviewModal } from "./components/ProfilePreviewModal";

export interface LawyerPreviewData {
  title: string;
  firstName: string;
  lastName: string;
  roleTitle: string;
  firmName: string;
  firmAddress: string;
  firmCity: string;
  firmState: string;
  firmZip: string;
  photoUrl: string | null;
  firmLogoUrl: string | null;
  email: string;
  phone: string;
  licensedStates: { state: string; bar_number: string }[];
}

const NAV_ITEMS = [
  { id: "profile", label: "Your profile" },
  { id: "notifications", label: "Notifications" },
  { id: "security", label: "Security" },
  { id: "help", label: "Help" },
] as const;

type SectionId = (typeof NAV_ITEMS)[number]["id"];

export const LawyerSettingsPage: FC = () => {
  const { user, authUser } = useAppContext();
  const { lawyer } = useLawyerSettings();
  const { data: licensedStatesData } = useLawyerLicensedStates(lawyer?.id);
  const queryClient = useQueryClient();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [photoKey, setPhotoKey] = useState<string | null>(null);
  const [firmLogoKey, setFirmLogoKey] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<SectionId>("profile");
  const [isDirty, setIsDirty] = useState(false);
  const [liveFormData, setLiveFormData] = useState<ProfileFormData | null>(null);
  const saveRef = useRef<(() => Promise<boolean>) | null>(null);
  const resetRef = useRef<(() => void) | null>(null);

  const blocker = useBlocker(isDirty);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (lawyer?.profile_photo_key && !photoKey) setPhotoKey(lawyer.profile_photo_key);
    if (lawyer?.firm_logo_key && !firmLogoKey) setFirmLogoKey(lawyer.firm_logo_key);
  }, [lawyer, photoKey, firmLogoKey]);

  useEffect(() => {
    if (!isDirty) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const sectionRefs = useMemo(
    () => ({
      profile: { current: null as HTMLDivElement | null },
      notifications: { current: null as HTMLDivElement | null },
      security: { current: null as HTMLDivElement | null },
      help: { current: null as HTMLDivElement | null },
    }),
    [],
  );

  const { data: photoUrl } = useLawyerPhotoUrl(photoKey);
  const { data: firmLogoUrl } = useLawyerPhotoUrl(firmLogoKey);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    for (const item of NAV_ITEMS) {
      const el = sectionRefs[item.id].current;
      if (!el) continue;

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setActiveSection(item.id);
            }
          }
        },
        { threshold: 0.2, rootMargin: "-80px 0px -60% 0px" },
      );

      observer.observe(el);
      observers.push(observer);
    }

    return () => observers.forEach((o) => o.disconnect());
  }, [sectionRefs]);

  const scrollToSection = (id: SectionId) => {
    sectionRefs[id].current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const handleScrollToCellPhone = useCallback(() => {
    const el = document.getElementById("cell-phone");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus();
    }
  }, []);

  const previewData: LawyerPreviewData = {
    title: liveFormData?.title ?? lawyer?.title ?? "",
    firstName: liveFormData?.firstName ?? user?.first_name ?? "",
    lastName: liveFormData?.lastName ?? user?.last_name ?? "",
    roleTitle: liveFormData?.roleTitle ?? lawyer?.role_title ?? "",
    firmName: liveFormData?.firmName ?? lawyer?.firm_name ?? "",
    firmAddress: lawyer?.firm_address ?? "",
    firmCity: lawyer?.firm_city ?? "",
    firmState: lawyer?.firm_state ?? "",
    firmZip: lawyer?.firm_zip ?? "",
    photoUrl: photoUrl ?? null,
    firmLogoUrl: firmLogoUrl ?? null,
    email: authUser?.email ?? user?.email ?? "",
    phone: liveFormData?.workPhone ?? user?.phone_number ?? "",
    licensedStates:
      liveFormData?.licensedStates ??
      (licensedStatesData ?? []).map((s) => ({
        state: s.state,
        bar_number: s.bar_number,
      })),
  };

  return (
    <LawyerLayout title="Settings">
      <div className="flex gap-6 min-w-0">
        <nav className="w-[175px] shrink-0 hidden md:flex flex-col gap-1 sticky top-0 self-start border-l border-primary">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={cn(
                "border-l-2 px-[18px] py-[9px] text-left text-sm transition-colors",
                activeSection === item.id
                  ? "border-brand font-medium text-primary"
                  : "border-transparent text-secondary hover:text-primary",
              )}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0 flex flex-col gap-6">
          <div
            ref={(el) => {
              sectionRefs.profile.current = el;
            }}
          >
            <ProfileSection
              onSaved={() => setPreviewOpen(false)}
              onSaveAndPreview={() => setPreviewOpen(true)}
              photoUrl={photoUrl ?? null}
              firmLogoUrl={firmLogoUrl ?? null}
              onPhotoKeyChange={(key) => {
                setPhotoKey(key);
                queryClient.invalidateQueries({ queryKey: ["lawyer-photo-url", key] });
              }}
              onFirmLogoKeyChange={(key) => {
                setFirmLogoKey(key);
                queryClient.invalidateQueries({ queryKey: ["lawyer-photo-url", key] });
              }}
              onIsDirtyChange={setIsDirty}
              onFormChange={setLiveFormData}
              saveRef={saveRef}
              resetRef={resetRef}
            />
          </div>

          <div
            ref={(el) => {
              sectionRefs.notifications.current = el;
            }}
          >
            <NotificationsSection onScrollToCellPhone={handleScrollToCellPhone} />
          </div>

          <div
            ref={(el) => {
              sectionRefs.security.current = el;
            }}
          >
            <SecuritySection />
          </div>

          <div
            ref={(el) => {
              sectionRefs.help.current = el;
            }}
          >
            <HelpSection />
          </div>
        </div>
      </div>

      <ProfilePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onSave={async () => {
          const success = await saveRef.current?.();
          if (success) setPreviewOpen(false);
        }}
        isDirty={isDirty}
        data={previewData}
      />

      <Dialog
        open={blocker.state === "blocked"}
        onOpenChange={(isOpen) => {
          if (!isOpen) blocker.reset?.();
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes to your profile. If you leave now, your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3">
            <Button variant="outline" size="md" onClick={() => blocker.reset?.()}>
              Keep editing
            </Button>
            <Button
              variant="destructive"
              size="md"
              onClick={() => {
                resetRef.current?.();
                blocker.proceed?.();
              }}
            >
              Leave without saving
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </LawyerLayout>
  );
};
